import { cn } from "@faire/prototype-playground/lib/utils";
import React from "react";

import {
  PADX,
  SECTION_TITLE_TYPE,
  TILE_HOVER_GROUP,
  TILE_HOVER_IMAGE,
} from "../constants";
import { type PriceMode } from "../PriceLabel";
import { ProductCard, type ProductCardData } from "../ProductCard";

export type CollectionOverlayTreatment =
  | "scrim-top"
  | "scrim-bottom"
  | "plate"
  | "caption";

interface FeaturedCollectionProps {
  /** Collection name — overlaid on the large image. See `overlayTreatment`
   * for where it sits and how it's kept legible. */
  title: string;
  /** Optional one-line collection blurb, under the title. */
  subtitle?: string;
  /** Large image (left half). */
  imageUrl?: string;
  /** Up to six products shown in the right-hand 3×2 grid. */
  products: ProductCardData[];
  priceMode: PriceMode;
  /**
   * When set, each card links to `linkFor(product)` instead of opening the
   * sign-up gate — same contract as BestsellersRow, so a surface can route
   * both module kinds to the same destination.
   */
  linkFor?: (product: ProductCardData) => string;
  /** See `BrandDataset.productImageAspect` — also loosens the collection
   * cover image's mobile aspect ratio from landscape (4:3) to portrait
   * (3:4), since an apparel brand's lifestyle photography is typically shot
   * portrait too. Desktop is already flexible (stretches to the product
   * grid's height). */
  aspectRatio?: "square" | "portrait";
  /** Drop the overlaid title and its scrim. For covers the brand typeset
   * themselves — the collection name is already set into the artwork, so the
   * overlay just prints it twice and the gradient dirties their photograph. */
  hideTitleOverlay?: boolean;
  /**
   * How the collection name is made legible against the cover photo. Every
   * option clears 4.5:1 against a blown-out white photo; they differ in how
   * much of the photograph they spend to get there.
   *
   *   "scrim-bottom" — the default. Darkens the foot of the image, name at
   *                    bottom-left. Brands shoot the subject's face in the
   *                    upper third, so a top scrim spends the part of the frame
   *                    that sells the product while the sparser lower third
   *                    goes unused (Ryan, 2026-09-17).
   *   "scrim-top"     — the same band mirrored to the top edge. For covers
   *                    whose subject sits low in the frame.
   *   "plate"        — a blurred panel sized to the name. Spends the least
   *                    photograph, and the blur kills the high-frequency
   *                    detail a flat scrim can't fix.
   *   "caption"      — name below the image in brand ink. No scrim at all, so
   *                    the photograph is untouched and legibility stops
   *                    depending on the photo entirely.
   */
  overlayTreatment?: CollectionOverlayTreatment;
  /**
   * Cover geometry on desktop.
   *   "stretch" — the cover grows to the product grid's height (the default,
   *               and what every brand piped through here was drawn against).
   *   "square"  — a true 1:1 cover, centred against the taller product grid.
   *               For brands whose collection covers are square artwork with
   *               the collection name typeset into them: stretching those to
   *               the grid's height crops the name off both edges, because
   *               `object-cover` has to lose width to gain height (Ryan,
   *               2026-09-02).
   */
  coverAspect?: "stretch" | "square";
  /**
   * Cover geometry while the module is stacked (below `lg`), where the cover
   * sits above the tiles at the page's full width rather than beside them.
   *
   * Defaults to the per-brand ratio described on `aspectRatio`. `"square"`
   * overrides it: a 4:3 cover on a phone is a letterbox strip that gives the
   * brand's photography less height than a single product tile below it gets
   * (Ryan, 2026-09-21). Desktop is untouched — that's `coverAspect`.
   */
  stackedCoverAspect?: "default" | "square";
  /**
   * Aspect for the six product tiles, when it should differ from the brand's
   * `productImageAspect`. Defaults to that, so nothing changes unless asked.
   *
   * Exists because the tiles set the module's height, and beside a square
   * cover a portrait tile makes the two columns badly unequal: at 664px wide,
   * 3x2 portrait tiles stand 832 tall against a 664 cover, where square tiles
   * come to 621 (Ryan, 2026-09-02). Squaring them here also crops in on
   * product photography that is mostly empty backdrop at 2:3.
   */
  tileImageAspect?: "square" | "portrait";
  /** See BestsellersRow — showcase tuning for the brand.com surface. */
  lifestyleEvery?: number;
  hideDetails?: boolean;
  /**
   * How the six product tiles sit under the cover when the module is stacked
   * (below `lg` / 1024). Desktop is always the 3×2 beside the cover.
   *
   *   "grid"     — two-up wrap under the square (the default).
   *   "carousel" — a 2.5-tile peek, scrolling off the right edge.
   */
  stackedProducts?: "grid" | "carousel";
  /**
   * Whether the tiles sit on a tinted surface and so drop their faint grey
   * image scrim. Defaults to true, which is what every brand drawn against the
   * tertiary-surface default expects. Set false when `backgroundColor` is the
   * page's own white, where packshots need the scrim for an edge.
   */
  onTintedSurface?: boolean;
  /** Defaults to the neutral tertiary surface — a brand tint here competes
   * with the collection's own photography and product tiles (Ryan,
   * 2026-08-13). Override only when a module genuinely needs its own color. */
  backgroundColor?: string;
}

/**
 * The scrim sets a floor on *average* contrast, but a hard bright edge inside
 * the photo — a window mullion, a chrome rail — can still land directly behind
 * a letter and fail locally. This soft halo covers that without reading as a
 * drop shadow on the type. Deliberately the same value the hero uses in
 * `Masthead`: a tighter, more opaque pass separates the glyphs well enough to
 * be visible as an edge, which no other overlaid type in the framework does.
 */
const OVERLAY_TEXT_SHADOW = "0 2px 24px rgba(0,0,0,0.35)";

/**
 * Stops are in px, not percentages, and hold flat across the text band before
 * easing out. Percentages tie the scrim's shape to the cover's height, so the
 * gradient that worked on a 620px desktop cover had already faded to 0.36 alpha
 * under the subtitle — against a blown-out white photo that is 2.6:1, where
 * 14px copy needs 4.5:1. 0.58 black over white composites to 5.3:1, so the
 * plateau is the contrast floor every brand's photography inherits, and it has
 * to outlast the tallest text block on the shortest cover. The four easing
 * stops exist so the falloff has no visible band edge (Ryan, 2026-09-17).
 *
 * 190px is that tallest block, measured from whichever edge the scrim hangs off
 * so the bottom treatment — where the title is the line furthest from the edge —
 * gets the same floor as the top one from one set of stops. It adds up as the
 * 32px inset, the 40px CTA, its 16px gap, a blurb of up to three lines, and the
 * title: 182px worst case.
 */
const SCRIM_STOPS =
  "rgba(0,0,0,0.60) 0px, rgba(0,0,0,0.58) 190px, rgba(0,0,0,0.44) 240px, rgba(0,0,0,0.27) 285px, rgba(0,0,0,0.11) 320px, rgba(0,0,0,0) 350px";

const SCRIM_HEIGHT = "h-[350px]";

/** A blur alone doesn't darken a white photo — it only removes the detail — so
 * the plate still carries the same alpha floor as the scrim. What it buys is
 * area: it spends only the text's own footprint of photograph. */
const PLATE_BACKGROUND = "rgba(0,0,0,0.58)";

const CollectionScrim: React.FC<{ edge: "top" | "bottom" }> = ({ edge }) => (
  <div
    className={cn(
      "pointer-events-none absolute inset-x-0",
      SCRIM_HEIGHT,
      edge === "top" ? "top-0" : "bottom-0"
    )}
    style={{
      background: `linear-gradient(${
        edge === "top" ? "180deg" : "0deg"
      }, ${SCRIM_STOPS})`,
    }}
  />
);

const SHOP_COLLECTION_LABEL = "Shop collection";

/**
 * Slate's own button classes on a `<span>`, not a `<Button>`. The whole cover is
 * already the `data-signup-gate` button, and nesting a real control inside it is
 * invalid HTML and would swallow the gate's click — but `.fs-button` styles
 * nothing element-specific, so borrowing the classes keeps Slate's exact
 * geometry (40px tall at `small`, `rounded-fs-component-default`) without the
 * element. The secondary variant's white surface carries its own contrast rather
 * than leaning on the scrim, so the CTA survives the same range of photography,
 * and its border keeps it legible on the caption treatment's light background
 * too (Ryan, 2026-09-17).
 */
const ShopCollectionCta: React.FC = () => (
  <div className="mt-4">
    <span className="fs-button fs-button--secondary fs-button--small">
      {SHOP_COLLECTION_LABEL}
    </span>
  </div>
);

/** The overlaid name, in white with the shadow insurance. `subtitle` was
 * accepted and documented but never rendered, so every brand's collection blurb
 * was silently dropped (Ryan, 2026-09-02). */
const OverlaidName: React.FC<{ title: string; subtitle?: string }> = ({
  title,
  subtitle,
}) => (
  <>
    <p
      className={SECTION_TITLE_TYPE}
      style={{ color: "#ffffff", textShadow: OVERLAY_TEXT_SHADOW }}
    >
      {title}
    </p>
    {subtitle ? (
      // 52ch, not 42. The cap is here to stop a blurb running the full width
      // of a wide cover, but 42 was cutting in well before the photograph ran
      // out — a one-sentence blurb broke onto a second line with ~300px of
      // cover still to its right, which reads as the text hitting an edge
      // that isn't there. 52ch clears a typical blurb on one line and is
      // still inside a comfortable measure; anything genuinely long still
      // wraps (Ryan, 2026-09-21).
      <p
        className="type-fs-paragraph mt-1 max-w-[52ch]"
        style={{ color: "#ffffff", textShadow: OVERLAY_TEXT_SHADOW }}
      >
        {subtitle}
      </p>
    ) : null}
    <ShopCollectionCta />
  </>
);

/** Caption treatment: the name sits on the module's own background, so it takes
 * brand ink and needs neither scrim nor shadow. */
const CaptionName: React.FC<{ title: string; subtitle?: string }> = ({
  title,
  subtitle,
}) => (
  <div className="pt-5">
    <p className={cn(SECTION_TITLE_TYPE, "text-fs-text-primary")}>{title}</p>
    {subtitle ? (
      <p className="type-fs-paragraph text-fs-text-subdued mt-1 max-w-[72ch]">
        {subtitle}
      </p>
    ) : null}
    <ShopCollectionCta />
  </div>
);

/**
 * "Big collection" module: a 50/50 split with one large collection image on the
 * left (name, blurb and CTA overlaid — bottom-left by default, see
 * `overlayTreatment`) beside a 3×2 grid of six product tiles.
 * Used to feature a Faire product-based collection (e.g. "Tried & True Best
 * Sellers") near the top of the brand home. Image + tiles are sign-up gated like
 * the rest of the logged-out home.
 */
export const FeaturedCollection: React.FC<FeaturedCollectionProps> = ({
  title,
  subtitle,
  imageUrl,
  products,
  priceMode,
  linkFor,
  aspectRatio,
  coverAspect = "stretch",
  stackedCoverAspect = "default",
  tileImageAspect,
  backgroundColor = "var(--slate-color-surface-tertiary)",
  hideTitleOverlay = false,
  overlayTreatment = "scrim-bottom",
  lifestyleEvery,
  hideDetails,
  stackedProducts = "grid",
  onTintedSurface = true,
}) => {
  // `hideTitleOverlay` outranks the treatment: it means the cover already
  // carries the collection's name, so there is nothing to make legible.
  const treatment = hideTitleOverlay ? "none" : overlayTreatment;
  // Caption is the one treatment that needs height outside the image, so the
  // cover's aspect ratio moves off the button and onto the image's own box.
  const isCaption = treatment === "caption";
  // The stacked crop, shared by both branches below so they can't drift.
  const stackedAspect =
    stackedCoverAspect === "square"
      ? "aspect-square"
      : aspectRatio === "portrait"
        ? "aspect-[3/4]"
        : "aspect-[4/3]";

  const cover = (
    <>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={title}
          className={cn(
            "absolute inset-0 h-full w-full object-cover",
            TILE_HOVER_IMAGE
          )}
        />
      ) : null}
      {treatment === "scrim-top" || treatment === "scrim-bottom" ? (
        <>
          <CollectionScrim
            edge={treatment === "scrim-top" ? "top" : "bottom"}
          />
          <div
            className={cn(
              // 20px on a phone, 32px from `md` up. The 32px inset was
              // drawn against the desktop cover; on a 360px tile it
              // crowds the name into the middle of the photograph
              // (Ryan, 2026-09-21).
              "absolute left-5 pr-5 md:left-8 md:pr-8",
              treatment === "scrim-top"
                ? "top-5 md:top-8"
                : "bottom-5 md:bottom-8"
            )}
          >
            <OverlaidName title={title} subtitle={subtitle} />
          </div>
        </>
      ) : null}
      {treatment === "plate" ? (
        // Width is left to the content so the plate hugs the name instead of
        // banding the cover; the cap keeps it inside the opposite inset.
        <div
          className="absolute top-5 left-5 max-w-[calc(100%-2.5rem)] rounded p-5 backdrop-blur-md md:top-8 md:left-8 md:max-w-[calc(100%-4rem)]"
          style={{ background: PLATE_BACKGROUND }}
        >
          <OverlaidName title={title} subtitle={subtitle} />
        </div>
      ) : null}
    </>
  );

  return (
    <div
      className={cn("py-4 md:py-10 lg:py-[48px] 2xl:py-[80px]", PADX)}
      style={{ backgroundColor }}
    >
      <div
        className={cn(
          "grid grid-cols-1 gap-[var(--grid-gutter,16px)] lg:grid-cols-2",
          // Square mode can't stretch the cover to the row without pulling it
          // back out of square, so the two columns are whatever height they are
          // and both hang from the top edge — the shared line the eye actually
          // reads them against (Ryan, 2026-09-02).
          coverAspect === "square"
            ? "items-stretch lg:items-start"
            : "items-stretch"
        )}
      >
        <button
          type="button"
          data-signup-gate
          className={cn(
            "relative block min-h-[300px] text-left",
            TILE_HOVER_GROUP,
            // Caption mode's rounding and clipping belong to the image box
            // instead, so the copy underneath isn't inside the rounded corner.
            isCaption
              ? "h-full"
              : cn(
                  "bg-fs-surface-secondary overflow-hidden rounded",
                  coverAspect === "square"
                    ? "aspect-square"
                    : cn("h-full lg:aspect-auto", stackedAspect)
                )
          )}
        >
          {isCaption ? (
            <div className="flex h-full flex-col">
              <div
                className={cn(
                  "bg-fs-surface-secondary relative w-full overflow-hidden rounded",
                  coverAspect === "square"
                    ? "aspect-square"
                    : cn("flex-1 lg:aspect-auto", stackedAspect)
                )}
              >
                {cover}
              </div>
              <CaptionName title={title} subtitle={subtitle} />
            </div>
          ) : (
            cover
          )}
        </button>
        {stackedProducts === "carousel" ? (
          // Stacked only — at `lg` the 3×2 beside the cover takes over.
          // Width is the padded column plus the right page margin, so the
          // third tile peeks off the viewport edge instead of dying at the
          // inset. 2.5 tiles + two gutters fill that band.
          <div className="flex w-[calc(100%+16px)] gap-x-[var(--grid-gutter,16px)] overflow-x-auto [scrollbar-width:none] snap-x snap-mandatory [-ms-overflow-style:none] pr-4 md:w-[calc(100%+40px)] md:pr-10 lg:hidden [&::-webkit-scrollbar]:hidden">
            {/* The 2.5-tile math reads the gutter, so a retuned gutter keeps
                the third tile peeking by the same amount. */}
            {products.slice(0, 6).map((card, index) => (
              <div
                key={card.token}
                className="min-w-0 w-[calc((100%-2*var(--grid-gutter,16px))/2.5)] shrink-0 snap-start"
              >
                <ProductCard
                  product={card}
                  priceMode={priceMode}
                  href={linkFor?.(card)}
                  onTintedSurface={onTintedSurface}
                  aspectRatio={tileImageAspect ?? aspectRatio}
                  showLifestyleImage={
                    Boolean(lifestyleEvery) &&
                    index % (lifestyleEvery as number) === 1
                  }
                  hideDetails={hideDetails}
                />
              </div>
            ))}
          </div>
        ) : null}
        <div
          className={cn(
            "flex flex-wrap gap-x-[var(--grid-gutter,16px)] gap-y-6",
            stackedProducts === "carousel" ? "hidden lg:flex" : null
          )}
        >
          {products.slice(0, 6).map((card, index) => (
            // flex-wrap (not a fixed-column grid) so a partial last row — e.g. 5
            // products in a 3-across layout — never leaves a reserved empty cell.
            <div
              key={card.token}
              className="min-w-0 shrink-0 grow-0 basis-[calc(50%-var(--grid-gutter,16px)/2)] lg:basis-[calc(33.333%-var(--grid-gutter,16px)*2/3)]"
            >
              <ProductCard
                product={card}
                priceMode={priceMode}
                href={linkFor?.(card)}
                onTintedSurface={onTintedSurface}
                aspectRatio={tileImageAspect ?? aspectRatio}
                showLifestyleImage={
                  Boolean(lifestyleEvery) &&
                  index % (lifestyleEvery as number) === 1
                }
                hideDetails={hideDetails}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
